// src/pages/Teams.js — fixed: delivery persons need password, hard delete added
// + device-approval security: pending requests panel, device ID column, unblock
import React, { useState, useEffect } from 'react';
import { get, post, patch, del } from '../utils/api';
import { Spinner, Alert, Modal, Input, Select } from '../components/Shared';

const ROLES = [
  { value:'stock_removal',  label:'Stock Removal' },
  { value:'checking',       label:'Checking' },
  { value:'booking',        label:'Booking' },
  { value:'billing',        label:'Billing' },
  { value:'delivery_person',label:'Delivery Person' },
  { value:'team_leader',    label:'Team Leader' },
];

const TEAM_IDS = {
  stock_removal:   '00000000-0000-0000-0000-000000000001',
  checking:        '00000000-0000-0000-0000-000000000002',
  booking:         '00000000-0000-0000-0000-000000000005',
  billing:         '00000000-0000-0000-0000-000000000006',
  delivery_person: '00000000-0000-0000-0000-000000000004',
};

// NOTE: role-color-coding left untouched — meaningful, established
// color-code for distinguishing team roles at a glance, same reasoning
// as invoice stage colors.
const ROLE_COLORS = {
  owner:           'bg-red-100 text-red-700',
  team_leader:     'bg-purple-100 text-purple-700',
  stock_removal:   'bg-yellow-100 text-yellow-700',
  checking:        'bg-blue-100 text-blue-700',
  booking:         'bg-teal-100 text-teal-700',
  billing:         'bg-pink-100 text-pink-700',
  delivery_person: 'bg-green-100 text-green-700',
};

// The checkbox categories exposed in the Permissions modal. Owner and
// Team Leader always have full access regardless of what's stored here
// (enforced server-side in middleware/auth.js) — these toggles only
// actually matter for other roles.
const PERMISSION_CATEGORIES = [
  { key: 'dashboard',      label: 'Dashboard & Overview', desc: 'Dashboard, Invoice Details, Exceptions' },
  { key: 'stock_removal', label: 'Stock Removal',      desc: 'Stock Removal queue, pick and complete' },
  { key: 'checking',      label: 'Checking',           desc: 'Checking queue, Checking Assistant, label printing' },
  { key: 'dispatch',       label: 'Dispatch & Delivery',  desc: 'Assign Deliveries, Dispatch Overview, Live Map' },
  { key: 'vouchers',       label: 'Vouchers',             desc: 'Voucher Manager' },
  { key: 'reports',        label: 'Reports & Analytics',  desc: 'Reports, Heatmap' },
  { key: 'stage_override', label: 'Stage Override',       desc: 'Bulk multi-select stage override — powerful, sensitive action' },
  { key: 'admin_settings', label: 'Admin & Settings',     desc: 'Settings, Team Management, Transports, Marg Sync status' },
  { key: 'inward_register', label: 'Inward Register',     desc: 'Log supplier consignments against Marg purchase entries (L.R., articles, short/excess)' },
];

function shortId(id) {
  return id ? id.slice(0, 8) : '';
}

export default function Teams() {
  const [users,   setUsers]   = useState([]);
  const [teams,   setTeams]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [msg,     setMsg]     = useState(null);
  const [form,    setForm]    = useState({ name:'', phone:'', role:'stock_removal', password:'' });
  const [saving,  setSaving]  = useState(false);
  const [search,  setSearch]  = useState('');
  const [delUser,    setDelUser]    = useState(null);
  const [permUser,   setPermUser]   = useState(null);
  const [permDraft,  setPermDraft]  = useState({});
  const [savingPerm, setSavingPerm] = useState(false);
  const [pwUser,     setPwUser]     = useState(null);
  const [pwValue,    setPwValue]    = useState('');
  const [savingPw,   setSavingPw]   = useState(false);

  // Device-approval state
  const [deviceRequests, setDeviceRequests] = useState([]);
  const [denyTarget,     setDenyTarget]     = useState(null); // the request being denied
  const [denyReason,     setDenyReason]     = useState('');
  const [denying,        setDenying]        = useState(false);
  const [approving,      setApproving]      = useState(null); // request id currently approving
  const [editDeviceUser, setEditDeviceUser] = useState(null); // user whose device ID is being manually edited
  const [editDeviceValue,setEditDeviceValue]= useState('');
  const [savingDevice,   setSavingDevice]   = useState(false);
  const [unblockUser,    setUnblockUser]    = useState(null);
  const [unblocking,     setUnblocking]     = useState(false);

  async function load() {
    try {
      const [uData, tData, dData] = await Promise.all([
        get('/api/users?limit=200'),
        get('/api/teams'),
        get('/api/users/device-requests'), // defaults to pending only
      ]);
      setUsers(uData.users || []);
      setTeams(tData.teams || []);
      setDeviceRequests(dData.requests || []);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function addUser(e) {
    e.preventDefault();
    if (!form.password) {
      setMsg({ type:'error', text:'Password is required for all users including delivery persons' });
      return;
    }
    setSaving(true);
    try {
      await post('/api/users', {
        name:     form.name,
        phone:    form.phone,
        role:     form.role,
        password: form.password,
        team_id:  TEAM_IDS[form.role] || null,
      });
      setMsg({ type:'success', text:`${form.name} added successfully` });
      setShowAdd(false);
      setForm({ name:'', phone:'', role:'stock_removal', password:'' });
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally { setSaving(false); }
  }

  async function toggleActive(user) {
    try {
      await patch(`/api/users/${user.id}`, { is_active: !user.is_active });
      setMsg({ type:'success', text:`${user.name} ${user.is_active ? 'marked absent' : 'marked present'}` });
      load();
    } catch (err) { setMsg({ type:'error', text: err.message }); }
  }

  async function deleteUser(user) {
    try {
      await del(`/api/users/${user.id}`);
      setMsg({ type:'success', text:`${user.name} deleted` });
      setDelUser(null);
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
      setDelUser(null);
    }
  }

  function openPermissions(user) {
    setPermUser(user);
    setPermDraft(user.permissions || {});
  }

  function togglePermDraft(key) {
    setPermDraft(prev => ({ ...prev, [key]: !prev[key] }));
  }

  async function savePermissions() {
    setSavingPerm(true);
    try {
      await patch(`/api/users/${permUser.id}`, { permissions: permDraft });
      setMsg({ type:'success', text:`Permissions updated for ${permUser.name}` });
      setPermUser(null);
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally {
      setSavingPerm(false);
    }
  }

  async function resetPassword() {
    if (!pwValue || pwValue.length < 6) {
      setMsg({ type:'error', text:'New password must be at least 6 characters' });
      return;
    }
    setSavingPw(true);
    try {
      await post(`/api/users/${pwUser.id}/reset-password`, { newPassword: pwValue });
      setMsg({ type:'success', text:`Password reset for ${pwUser.name} — they'll be asked to set a new one on next login` });
      setPwUser(null);
      setPwValue('');
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally {
      setSavingPw(false);
    }
  }

  // ── Device-approval actions ──────────────────────────────────────────
  async function approveRequest(reqRow) {
    setApproving(reqRow.id);
    try {
      await post(`/api/users/device-requests/${reqRow.id}/approve`);
      setMsg({ type:'success', text:`Device approved for ${reqRow.user_name}` });
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally {
      setApproving(null);
    }
  }

  async function submitDeny() {
    if (!denyReason.trim()) {
      setMsg({ type:'error', text:'A reason is required to deny a device request' });
      return;
    }
    setDenying(true);
    try {
      await post(`/api/users/device-requests/${denyTarget.id}/deny`, { reason: denyReason.trim() });
      setMsg({ type:'success', text:`Device request denied for ${denyTarget.user_name}` });
      setDenyTarget(null);
      setDenyReason('');
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally {
      setDenying(false);
    }
  }

  function openEditDevice(user) {
    setEditDeviceUser(user);
    setEditDeviceValue(user.trusted_device_id || '');
  }

  async function saveDeviceEdit() {
    setSavingDevice(true);
    try {
      await patch(`/api/users/${editDeviceUser.id}`, { trusted_device_id: editDeviceValue.trim() || null });
      setMsg({ type:'success', text:`Trusted device updated for ${editDeviceUser.name}` });
      setEditDeviceUser(null);
      setEditDeviceValue('');
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally {
      setSavingDevice(false);
    }
  }

  async function submitUnblock() {
    setUnblocking(true);
    try {
      await post(`/api/users/${unblockUser.id}/unblock`);
      setMsg({ type:'success', text:`${unblockUser.name} has been unblocked` });
      setUnblockUser(null);
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally {
      setUnblocking(false);
    }
  }

  const filtered = users.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.phone?.includes(search) ||
    u.role?.includes(search)
  );

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Team Management</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {users.filter(u => u.is_active).length} present today · All users login with phone + password
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-brand-700 text-white text-sm rounded-lg hover:bg-brand-500 font-semibold"
        >
          + Add Team Member
        </button>
      </div>

      {msg && <div className="mb-4"><Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} /></div>}

      {/* Pending device approvals — only shown when there's something
          waiting, so it doesn't clutter the page most days */}
      {deviceRequests.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-amber-300 dark:border-amber-700 overflow-hidden mb-6">
          <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
            <p className="text-sm font-bold text-amber-800 dark:text-amber-300">
              ⏳ Pending Device Approvals ({deviceRequests.length})
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              These delivery app logins are on an unrecognized device and are waiting for a decision.
            </p>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {deviceRequests.map(r => (
                <tr key={r.id} className="border-t border-amber-100 dark:border-amber-900">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{r.user_name}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{r.user_phone}</td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-400">{shortId(r.device_id)}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {new Date(r.requested_at).toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => approveRequest(r)}
                        disabled={approving === r.id}
                        className="text-xs px-3 py-1.5 rounded font-semibold bg-green-600 text-white hover:bg-green-500 disabled:opacity-60"
                      >
                        {approving === r.id ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => { setDenyTarget(r); setDenyReason(''); }}
                        className="text-xs px-3 py-1.5 rounded font-semibold bg-red-50 text-red-600 hover:bg-red-100"
                      >
                        Deny
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Team summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {teams.map(t => (
          <div key={t.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">{t.name}</p>
            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{t.active_members}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">of {t.max_members} max</p>
            <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-1 mt-2">
              <div className="h-1 rounded-full bg-brand-500"
                style={{ width:`${Math.min(100,(t.active_members/t.max_members)*100)}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Info box about delivery person login — left blue, matches the
          Alert component's "info" convention (info=blue) left untouched
          elsewhere in the app */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4">
        <p className="text-blue-700 text-sm font-medium">
          ℹ️ All team members including delivery persons now use <strong>phone + password</strong> to login.
          Delivery persons use the delivery app at <strong>http://localhost:3003</strong>
        </p>
      </div>

      {/* Users table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-clip">
        <div className="p-4 border-b sticky top-0 z-20 bg-white dark:bg-gray-800">
          <input
            type="text"
            placeholder="Search by name, phone or role..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              {['Name','Phone','Role','Team','Device','Last Login','Status','Actions'].map((h,i) => (
                <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{u.name}</td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{u.phone}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLORS[u.role] || 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                    {u.role?.replace(/_/g,' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{u.team_name || '—'}</td>
                <td className="px-4 py-3 text-xs">
                  {u.role === 'delivery_person' ? (
                    <button
                      onClick={() => openEditDevice(u)}
                      className="font-mono text-gray-500 dark:text-gray-400 hover:text-brand-700 hover:underline"
                      title="Click to manually set this user's trusted device"
                    >
                      {u.trusted_device_id ? shortId(u.trusted_device_id) : 'not set'} ✎
                    </button>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('en-IN') : 'Never'}
                </td>
                <td className="px-4 py-3">
                  {u.is_blocked ? (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700"
                      title={u.blocked_reason || ''}>
                      Blocked
                    </span>
                  ) : (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                      {u.is_active ? 'Present' : 'Absent'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.role !== 'owner' && (
                    <div className="flex gap-2 flex-wrap">
                      {u.is_blocked && (
                        <button
                          onClick={() => setUnblockUser(u)}
                          className="text-xs px-2 py-1 rounded font-medium bg-red-600 text-white hover:bg-red-500"
                        >
                          Unblock
                        </button>
                      )}
                      <button
                        onClick={() => openPermissions(u)}
                        className="text-xs px-2 py-1 rounded font-medium bg-brand-700/5 text-brand-700 hover:bg-brand-700/10"
                      >
                        Permissions
                      </button>
                      <button
                        onClick={() => toggleActive(u)}
                        className={`text-xs px-2 py-1 rounded font-medium ${u.is_active ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
                      >
                        {u.is_active ? 'Mark Absent' : 'Mark Present'}
                      </button>
                      <button
                        onClick={() => { setPwUser(u); setPwValue(''); }}
                        className="text-xs px-2 py-1 rounded font-medium bg-purple-50 text-purple-700 hover:bg-purple-100"
                      >
                        Reset Password
                      </button>
                      <button
                        onClick={() => setDelUser(u)}
                        className="text-xs px-2 py-1 rounded font-medium bg-red-50 text-red-600 hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add user modal */}
      {showAdd && (
        <Modal title="Add Team Member" onClose={() => setShowAdd(false)}>
          <form onSubmit={addUser}>
            <Input
              label="Full Name"
              value={form.name}
              onChange={e => setForm({...form, name:e.target.value})}
              required
              placeholder="e.g. Ravi Kumar"
            />
            <Input
              label="Phone Number"
              value={form.phone}
              onChange={e => setForm({...form, phone:e.target.value})}
              required
              placeholder="+919876543210"
            />
            <Select
              label="Role"
              value={form.role}
              onChange={e => setForm({...form, role:e.target.value})}
              options={ROLES}
            />
            <Input
              label="Password"
              type="password"
              value={form.password}
              onChange={e => setForm({...form, password:e.target.value})}
              required
              placeholder="Set an initial password (can be reset later via Team Management)"
            />
            {form.role === 'delivery_person' && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                🚚 Delivery persons login with this phone number and password at <strong>http://localhost:3003</strong>. Use "Reset Password" in the table below if they ever need it changed.
                Their first login from the app will need approval below (Pending Device Approvals) unless you set their device ID manually.
              </div>
            )}
            <div className="flex gap-3 mt-2">
              <button type="button" onClick={() => setShowAdd(false)}
                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-60">
                {saving ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete confirmation modal */}
      {delUser && (
        <Modal title="Delete User" onClose={() => setDelUser(null)}>
          <div className="text-center py-4">
            <p className="text-5xl mb-4">⚠️</p>
            <p className="text-gray-800 dark:text-gray-100 font-semibold text-lg mb-2">Delete {delUser.name}?</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
              This permanently deletes the user account. Their historical records (invoices handled, deliveries made) will be preserved but their account will be removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDelUser(null)}
                className="flex-1 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteUser(delUser)}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-500"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* Permissions modal */}
      {permUser && (
        <Modal title={`Permissions — ${permUser.name}`} onClose={() => setPermUser(null)}>
          {permUser.role === 'owner' && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              ⚠️ Owners always have full access to everything, regardless of these checkboxes —
              this is a permanent safeguard so the account holder can never be locked out.
            </div>
          )}
          {permUser.role === 'team_leader' && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
              ℹ️ Team Leaders start with everything checked by default, but these ARE real,
              revocable permissions — uncheck anything here to actually restrict this person.
            </div>
          )}
          <div className="space-y-3 mb-5">
            {PERMISSION_CATEGORIES.map(cat => (
              <label key={cat.key}
                className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!permDraft[cat.key]}
                  onChange={() => togglePermDraft(cat.key)}
                  className="mt-0.5 w-4 h-4 accent-brand-700"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{cat.label}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{cat.desc}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setPermUser(null)}
              className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={savePermissions} disabled={savingPerm}
              className="flex-1 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-60">
              {savingPerm ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </Modal>
      )}
      {/* Reset password modal */}
      {pwUser && (
        <Modal title={`Reset Password — ${pwUser.name}`} onClose={() => { setPwUser(null); setPwValue(''); }}>
          <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-700">
            Set a new temporary password below. {pwUser.name} will be required to choose their own
            new password the next time they log in — you won't need to hand this exact one out
            permanently, and any device they're currently logged in on will be signed out.
          </div>
          <Input
            label="New Temporary Password"
            type="password"
            value={pwValue}
            onChange={e => setPwValue(e.target.value)}
            placeholder="At least 6 characters"
          />
          <div className="flex gap-3 mt-2">
            <button type="button" onClick={() => { setPwUser(null); setPwValue(''); }}
              className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={resetPassword} disabled={savingPw}
              className="flex-1 py-2 bg-purple-700 text-white rounded-lg text-sm font-semibold hover:bg-purple-600 disabled:opacity-60">
              {savingPw ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </Modal>
      )}

      {/* Deny device request modal — reason is required */}
      {denyTarget && (
        <Modal title={`Deny Device — ${denyTarget.user_name}`} onClose={() => { setDenyTarget(null); setDenyReason(''); }}>
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            A reason is required. After 3 denied device requests, this account is automatically blocked
            and will need an owner or team leader to unblock it.
          </div>
          <Input
            label="Reason for denial"
            value={denyReason}
            onChange={e => setDenyReason(e.target.value)}
            placeholder="e.g. Could not verify this was really them"
          />
          <div className="flex gap-3 mt-2">
            <button type="button" onClick={() => { setDenyTarget(null); setDenyReason(''); }}
              className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={submitDeny} disabled={denying}
              className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-500 disabled:opacity-60">
              {denying ? 'Denying...' : 'Deny Device'}
            </button>
          </div>
        </Modal>
      )}

      {/* Manually edit trusted device modal */}
      {editDeviceUser && (
        <Modal title={`Trusted Device — ${editDeviceUser.name}`} onClose={() => { setEditDeviceUser(null); setEditDeviceValue(''); }}>
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
            Ask {editDeviceUser.name} to open the Medji app — their device ID is shown at the bottom
            of the login screen. Type it here to trust that device immediately, bypassing the normal
            approval flow. Leave blank to clear it entirely (their next login will need approval again).
          </div>
          <Input
            label="Device ID"
            value={editDeviceValue}
            onChange={e => setEditDeviceValue(e.target.value)}
            placeholder="Paste or type the full device ID"
          />
          <div className="flex gap-3 mt-2">
            <button type="button" onClick={() => { setEditDeviceUser(null); setEditDeviceValue(''); }}
              className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={saveDeviceEdit} disabled={savingDevice}
              className="flex-1 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-60">
              {savingDevice ? 'Saving...' : 'Save Device ID'}
            </button>
          </div>
        </Modal>
      )}

      {/* Unblock confirmation modal */}
      {unblockUser && (
        <Modal title={`Unblock ${unblockUser.name}?`} onClose={() => setUnblockUser(null)}>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            This clears the block and resets their denial count to zero. Their next login attempt
            from any device will create a fresh pending approval request as normal.
          </p>
          <div className="flex gap-3">
            <button type="button" onClick={() => setUnblockUser(null)}
              className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={submitUnblock} disabled={unblocking}
              className="flex-1 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-60">
              {unblocking ? 'Unblocking...' : 'Unblock'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
